<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import RichText from '../../base/RichText/RichText.vue'
import type { PropType, Ref } from 'vue'
import {
  deleteMemorialMessage,
  type MemorialMessageDTO,
  type RemoveMemorialMessageResponse
} from '@/composables/memorize/memorize'
import { closeLoading, openLoading } from '../loading/loading'
import { useI18n } from 'vue-i18n'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { ElMessage, ElMessageBox } from 'element-plus'
import { renderMessage, isEmpty, isNotEmpty, cutTextAndEscape } from '@/utils/util'
import { currentUserHandle, getCurrentUser } from '@/composables/user/users'

const { t } = useI18n()

const props = defineProps({
  item: {
    type: Object as PropType<MemorialMessageDTO>,
    required: true
  },
  anim: {
    type: Boolean,
    default: false,
    required: false
  },
  canManage: {
    type: Boolean,
    default: true,
    required: false
  }
})

const animPlayed = ref(false)

watch(
  () => props.anim,
  (val) => {
    animPlayed.value = val
    setTimeout(() => {
      emit('animationEnd')
      console.log('animationEnd')
    }, 1500)
  }
)

const canDeleteMessage = computed(() => {
  return props.canManage || message.value.creator.id === currentUserHandle.value
})

const message = computed(() => props.item)

const reverse = ref(false)
const moreVisible = ref(false)
let moreRef = ref(null)

// deleteMemorialMessage
const emit = defineEmits(['onDelete', 'animationEnd', 'onThank', 'onCheckThank'])

function judgeDelete() {
  moreVisible.value = false
  ElMessageBox.confirm(t('m.confirm_delete_message'), {
    confirmButtonText: t('action.confirm'),
    cancelButtonText: t('action.cancel'),
    type: 'warning'
  })
    .then(() => {
      toDelete()
    })
    .catch(() => {})
}

async function toDelete() {
  moreVisible.value = false
  openLoading(t('alt.loading'))
  const { data, error, statusCode } = await deleteMemorialMessage(message.value.id)
  closeLoading()
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<RemoveMemorialMessageResponse>>
    if (resData.value.code === CODE_OK) {
      // success
      const response: RemoveMemorialMessageResponse = resData.value.data
      emit('onDelete', response.msgId)
      ElMessage({
        message: renderMessage(t('common.delete_success')),
        type: 'success'
      })
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}
</script>
<template>
  <div :class="animPlayed ? 'animated-background' : ''" flex="~ col" bg-theme-light rounded-3 p-4 items-center>
    <div v-if="isNotEmpty(message.content)" w-full>
      <div v-if="isNotEmpty(message.gift)" w-12 h-12 ml-2 mt-1 float-right>
        <GiftItem v-if="message.gift" :gift="message.gift" :isPreview="false" :size="12" />
      </div>
      <RichText :richText="message.content" text-theme-dark grow text-normal text-lg leading-8 break-words />
    </div>
    <div w-full mt-3 flex="~ row wrap" justify-start grow v-if="message.tags && message.tags.length > 0">
      <RTag v-for="(item, index) in message.tags" :key="index" :title="item.name" mr-2 :simple="true"></RTag>
    </div>
    <div w-full flex="~ row" mt-3>
      <SignatureItem
        :avatar="message.creator?.avatar"
        :wechatInfo="message.creator?.wechatUserInfo"
        :fullName="message.creator?.name?.fullName || ''"
        :relationName="message.relation?.name || ''"
        :displayTime="message.createdAt" />
      <div flex="~ row" justify-end items-center w-full>
        <div
          text-sm
          v-if="canDeleteMessage && message.creator?.id && message.creator.id !== getCurrentUser().value?.user?.id"
          @click="message.thanksId ? null : emit('onThank', message.id, message.creator?.name?.fullName || '')">
          {{ message.thanksId ? t('open_life.thanked') : t('obituary.thank') }}
        </div>
        <div ref="moreRef" ml-2>
          <RIcon
            v-if="canDeleteMessage"
            icon="i-iconamoon:menu-kebab-horizontal-fill"
            :reverse="reverse"
            :theme="true"
            size="medium"
            @click.prevent.stop="moreVisible = !moreVisible"></RIcon>
          <RToolTip :wrapper-ref="moreRef" placement="left" :arrow="false" v-model="moreVisible">
            <div flex flex-row items-center text-theme-dark>
              <div flex="~ row" items-center quick-menu-item @click="judgeDelete()" text-danger>
                <i i-mi:delete text-base></i>
                <p px-2>{{ $t('action.delete') }}</p>
              </div>
            </div>
          </RToolTip>
        </div>
      </div>
    </div>
    <div
      w-full
      rounded-2
      flex
      opacity-60
      bg-white
      items-center
      justify-between
      mt-4
      px-4
      py-2
      @click="emit('onCheckThank', message.thanks)"
      v-if="message.thanksId && (canDeleteMessage || message.creator?.id === getCurrentUser().value?.user?.id)">
      <div flex="~ row" items-center justify-start>
        <div class="i-proicons:checkmark" style="color: #292929" w-6 h-6 mr-3></div>
        <div text-base text-theme-dark>
          {{ cutTextAndEscape(t('memorize.host_thank_prefix') + message.thanks.contentText, 15) }}
        </div>
      </div>
      <div class="i-ep:arrow-right" style="color: #292929" w-4 h-4></div>
    </div>
  </div>
</template>
<style lang="scss" scoped></style>
